import { useAuth } from '@clerk/clerk-react';
import { onlineManager } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { v7 as uuidv7 } from 'uuid';
import {
  PIN_ERROR_CODES,
  PIN_MAX_FAILURES,
  verifyPin,
  type PinSwitchReportInput,
  type PinSwitchResult,
} from '@doorly/shared';
import { db } from '../../db/schema';
import { track } from '../../lib/analytics';
import { setActingAs } from '../../lib/actingAs';
import { apiClient, type ApiError } from '../../lib/apiClient';
import { clearLocalFailures, localLockRemainingMs, recordLocalFailure } from './pinLocalLock';

export type PinSwitchFailure =
  | { kind: 'invalid'; remaining: number }
  | { kind: 'locked'; retryAfterMs: number }
  | { kind: 'not_set' }
  | { kind: 'unavailable_offline' }
  | { kind: 'error'; message: string };

export interface SwitchTarget {
  id: string;
  firstName: string;
  lastName: string;
}

function isNetworkFailure(err: unknown): boolean {
  // `fetch` rejette par TypeError quand le réseau est coupé ; un `ApiError`
  // a toujours un `status`.
  return !(err as Partial<ApiError>).status;
}

function bodyOf(err: unknown): Record<string, unknown> {
  const body = (err as Partial<ApiError>).body;
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
}

/**
 * La bascule elle-même. Deux chemins, même résultat :
 *
 * - **en ligne** : `POST /pin/switch`, le serveur compare le code, tient le
 *   compteur d'échecs et journalise (`verifiedBy: server`) ;
 * - **hors ligne** (ou réseau tombé pendant l'appel) : comparaison locale
 *   contre le vérificateur en cache, compteur local, et la bascule est mise
 *   en file (`pin_switch`) pour être déclarée au retour du réseau
 *   (`verifiedBy: device`). Exigence N6 : c'est justement quand le wifi
 *   tombe qu'on ne veut pas retaper un mot de passe.
 *
 * Les deux appels partent au **niveau tablette** (`actingAs: null`) : c'est
 * la session du superviseur qui bascule, pas le vendeur précédent.
 */
export function usePinSwitch(deviceUserId: string | null) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);

  const complete = useCallback(
    (target: SwitchTarget, verified: 'server' | 'device') => {
      if (!deviceUserId) return;
      setActingAs(target.id, `${target.firstName} ${target.lastName}`, deviceUserId);
      track('pin_switch', { verified });
      // Rechargement complet plutôt qu'une invalidation de caches : toutes les
      // requêtes `me`, fiches, référentiels changent d'identité d'un coup, et
      // Dexie — donc la file hors ligne — survit au rechargement. Hors ligne, le
      // service worker sert l'app.
      window.location.replace('/leads');
    },
    [deviceUserId],
  );

  const switchOffline = useCallback(
    async (target: SwitchTarget, pin: string): Promise<PinSwitchFailure | null> => {
      const lockMs = await localLockRemainingMs(target.id);
      if (lockMs > 0) return { kind: 'locked', retryAfterMs: lockMs };
      const row = await db.pinVerifiers.get(target.id);
      if (!row) return { kind: 'unavailable_offline' };
      const ok = await verifyPin(pin, row.verifier);
      if (!ok) {
        const remaining = await recordLocalFailure(target.id);
        if (remaining === 0) {
          track('pin_switch_locked', {});
          return { kind: 'locked', retryAfterMs: await localLockRemainingMs(target.id) };
        }
        return { kind: 'invalid', remaining };
      }
      await clearLocalFailures(target.id);
      const now = new Date().toISOString();
      const payload: PinSwitchReportInput = { vendeurId: target.id, switchedAt: now };
      await db.mutations.add({
        id: uuidv7(),
        entity: 'pin_switch',
        op: 'create',
        entityId: target.id,
        payload,
        baseVersion: 0,
        createdAt: now,
        attempts: 0,
        nextAttemptAt: now,
        status: 'queued',
        actorUserId: null,
      });
      // Pas de flush ici : le rechargement qui suit interromprait l'envoi en
      // cours et laisserait la mutation `in_flight`. `SyncEngine.start()` la
      // reprend au démarrage suivant.
      complete(target, 'device');
      return null;
    },
    [complete],
  );

  const switchTo = useCallback(
    async (target: SwitchTarget, pin: string): Promise<PinSwitchFailure | null> => {
      if (!deviceUserId) return { kind: 'error', message: 'Session tablette introuvable.' };
      setBusy(true);
      try {
        if (!onlineManager.isOnline()) return await switchOffline(target, pin);
        // Verrou local d'abord, quel que soit le chemin.
        const lockMs = await localLockRemainingMs(target.id);
        if (lockMs > 0) return { kind: 'locked', retryAfterMs: lockMs };
        try {
          const token = await getToken();
          await apiClient.post<PinSwitchResult>(
            '/pin/switch',
            { vendeurId: target.id, pin },
            { token, actingAs: null },
          );
          await clearLocalFailures(target.id);
          complete(target, 'server');
          return null;
        } catch (err) {
          if (isNetworkFailure(err)) return await switchOffline(target, pin);
          const status = (err as ApiError).status;
          const body = bodyOf(err);
          // Le compteur local est la vue de CET appareil : il enregistre aussi
          // les échecs jugés par le serveur, sinon 4 essais en ligne + 4 hors
          // ligne feraient 8, et un verrou serveur s'évaporerait au premier
          // passage hors ligne.
          if (status === 423) {
            track('pin_switch_locked', {});
            const seconds =
              typeof body.retryAfterSeconds === 'number' ? body.retryAfterSeconds : 15 * 60;
            await db.pinAttempts.put({
              userId: target.id,
              failures: PIN_MAX_FAILURES,
              lockedUntil: new Date(Date.now() + seconds * 1000).toISOString(),
            });
            return { kind: 'locked', retryAfterMs: seconds * 1000 };
          }
          if (body.code === PIN_ERROR_CODES.invalid) {
            await recordLocalFailure(target.id);
            return {
              kind: 'invalid',
              remaining: typeof body.remaining === 'number' ? body.remaining : 0,
            };
          }
          if (body.code === PIN_ERROR_CODES.notSet) return { kind: 'not_set' };
          return {
            kind: 'error',
            message: err instanceof Error ? err.message : 'Bascule impossible.',
          };
        }
      } finally {
        setBusy(false);
      }
    },
    [deviceUserId, getToken, switchOffline, complete],
  );

  return { switchTo, busy };
}
